(function () {
  "use strict";

  var QuoteMath = globalThis.TaproomQuoteMath;
  if (!QuoteMath) throw new Error("Taproom quote math must load before the quote builder.");

  var DIGITAL_PRINT_OPTIONS = {
    one_large: { label: "One large print (back only on full-zip)", addon: "none" },
    one_small: { label: "One small print (left chest or sleeve)", addon: "none" },
    large_small: { label: "One large + one small print", addon: "small" },
    two_large: { label: "Two large prints (front + back)", addon: "large" },
    custom_review: { label: "Custom: 3+ locations, oversize, unusual, or zipper print", addon: "none", customReview: true }
  };

  var SCREEN_PLACEMENT_LABEL = {
    front: "Front",
    back: "Back",
    chest: "Left chest",
    sleeve: "Short sleeve"
  };

  function money(value) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(Number(value) || 0);
  }

  function integer(value, fallback) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function alphaLabel(index) {
    var value = index;
    var label = "";
    do {
      label = String.fromCharCode(65 + (value % 26)) + label;
      value = Math.floor(value / 26) - 1;
    } while (value >= 0);
    return label;
  }

  function parseGarments(value) {
    try {
      var parsed = JSON.parse(value || "[]");
      return parsed.filter(function (garment) {
        return garment && garment.id && garment.name && Number.isFinite(Number(garment.digitalAdjustment || garment.adjustment || 0));
      }).map(function (garment) {
        return {
          id: String(garment.id),
          name: String(garment.name),
          group: String(garment.group || "Garments"),
          digitalAdjustment: Number(garment.digitalAdjustment || garment.adjustment || 0),
          screenAdjustment: Number(garment.screenAdjustment || garment.adjustment || 0)
        };
      });
    } catch (error) {
      return [];
    }
  }

  function copyText(text, onDone) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(onDone).catch(function () {
        fallbackCopy(text, onDone);
      });
      return;
    }
    fallbackCopy(text, onDone);
  }

  function fallbackCopy(text, onDone) {
    var area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    try {
      document.execCommand("copy");
      onDone();
    } finally {
      document.body.removeChild(area);
    }
  }

  function initBuilder(root) {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    var tiers = QuoteMath.parseDigitalTiers(root.dataset.digitalTiers);
    var extraSmallLocation = Number(root.dataset.extraSmallLocation || root.dataset.extraLocation || 2.50);
    var extraLargeLocation = Number(root.dataset.extraLargeLocation || 3.25);
    var digitalMinimum = integer(root.dataset.digitalMinimum, 25);
    var digitalGroupMinimum = integer(root.dataset.digitalGroupMinimum, 12);
    var buyerPackagePrice = Number(root.dataset.buyerPackagePrice || 1);
    var screenMinimum = integer(root.dataset.screenMinimum, 72);
    var screenSetup = Number(root.dataset.screenSetup || 35);
    var contactEmail = root.dataset.contactEmail || "info@taproomthreads.com";
    var garments = parseGarments(root.dataset.garments);
    var defaultGarment = garments[0] || {
      id: "soft-cotton-tee",
      name: "Average soft cotton tee",
      group: "Tees",
      digitalAdjustment: 0,
      screenAdjustment: 0
    };
    if (!garments.length) garments = [defaultGarment];

    function findGarment(id) {
      return garments.find(function (garment) {
        return garment.id === id;
      }) || defaultGarment;
    }

    function renderGarmentOptions(selectedId) {
      var groups = {};
      garments.forEach(function (garment) {
        if (!groups[garment.group]) groups[garment.group] = [];
        groups[garment.group].push(garment);
      });
      return Object.keys(groups).map(function (group) {
        var options = groups[group].map(function (garment) {
          return '<option value="' + escapeHtml(garment.id) + '"' + (garment.id === selectedId ? " selected" : "") + ">" + escapeHtml(garment.name) + "</option>";
        }).join("");
        return '<optgroup label="' + escapeHtml(group) + '">' + options + "</optgroup>";
      }).join("");
    }

    var digitalState = [];
    var buyerPackages = 0;
    var screenState = {
      quantity: 72,
      garmentId: defaultGarment.id,
      tone: "light",
      locations: [{ placement: "front", colors: 1 }]
    };
    var latestDigitalSummary = "";
    var latestScreenSummary = "";
    var toastTimer = null;

    var digitalList = root.querySelector('[data-role="digital-design-list"]');
    var screenList = root.querySelector('[data-role="screen-location-list"]');
    var toast = root.querySelector('[data-role="toast"]');

    function showToast(message) {
      if (!toast) return;
      toast.textContent = message;
      toast.hidden = false;
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(function () {
        toast.hidden = true;
      }, 2400);
    }

    function addDigitalDesign(values) {
      var index = digitalState.length;
      digitalState.push({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2),
        name: values && values.name ? values.name : "Design " + alphaLabel(index),
        quantity: values && values.quantity ? values.quantity : 50,
        garmentId: values && values.garmentId ? values.garmentId : defaultGarment.id,
        printOption: values && values.printOption ? values.printOption : "one_large"
      });
      renderDigitalRows();
      calculateDigital();
    }

    function renderDigitalRows() {
      if (!digitalList) return;
      digitalList.innerHTML = digitalState.map(function (design, index) {
        var garmentOptions = renderGarmentOptions(design.garmentId);
        var options = Object.keys(DIGITAL_PRINT_OPTIONS).map(function (key) {
          var selected = key === design.printOption ? " selected" : "";
          return '<option value="' + key + '"' + selected + '>' + escapeHtml(DIGITAL_PRINT_OPTIONS[key].label) + "</option>";
        }).join("");

        return [
          '<div class="ttq__design-row" data-design-id="' + design.id + '">',
          '<div class="ttq__field"><label for="design-name-' + design.id + '">Design name</label>',
          '<input id="design-name-' + design.id + '" data-field="name" type="text" value="' + escapeHtml(design.name) + '" maxlength="60"></div>',
          '<div class="ttq__field"><label for="design-garment-' + design.id + '">Garment</label>',
          '<select id="design-garment-' + design.id + '" data-field="garmentId">' + garmentOptions + '</select></div>',
          '<div class="ttq__field"><label for="design-qty-' + design.id + '">Quantity</label>',
          '<input id="design-qty-' + design.id + '" data-field="quantity" type="number" min="1" max="10000" step="1" value="' + design.quantity + '"></div>',
          '<div class="ttq__field"><label for="design-print-' + design.id + '">Print setup</label>',
          '<select id="design-print-' + design.id + '" data-field="printOption">' + options + "</select></div>",
          '<button class="ttq__remove" type="button" data-action="remove-design" aria-label="Remove ' + escapeHtml(design.name) + '"' + (digitalState.length === 1 ? " disabled" : "") + ">X</button>",
          "</div>"
        ].join("");
      }).join("");
    }

    function renderTierLadder(totalQuantity, currentTier) {
      var container = root.querySelector('[data-role="tier-steps"]');
      if (!container) return;
      container.innerHTML = tiers.map(function (tier) {
        var classes = ["ttq__tier-step"];
        if (totalQuantity >= tier.min) classes.push("is-active");
        if (currentTier && currentTier.min === tier.min) classes.push("is-current");
        return '<div class="' + classes.join(" ") + '"><span class="ttq__tier-step-bar"></span><small>' + tier.min + "+</small></div>";
      }).join("");
    }

    function calculateDigital() {
      var estimate = QuoteMath.digitalEstimate({
        tiers: tiers,
        minimum: digitalMinimum,
        groupMinimum: digitalGroupMinimum,
        buyerPackagePrice: buyerPackagePrice,
        buyerPackages: buyerPackages,
        designs: digitalState.map(function (design) {
          var option = DIGITAL_PRINT_OPTIONS[design.printOption] || DIGITAL_PRINT_OPTIONS.one_large;
          var garment = findGarment(design.garmentId);
          var isFullZip = garment.id === "average-full-zip" || garment.id === "high-end-full-zip";
          var locationAddon = option.addon === "small"
            ? extraSmallLocation
            : option.addon === "large" ? extraLargeLocation : 0;
          return {
            name: design.name,
            quantity: Math.max(0, integer(design.quantity, 0)),
            garmentAdjustment: garment.digitalAdjustment,
            locationAddon: locationAddon,
            customReview: option.customReview === true || (isFullZip && design.printOption === "two_large")
          };
        })
      });
      var totalQuantity = estimate.totalQuantity;
      var tier = estimate.tier;
      var nextTier = estimate.nextTier;
      var tierPrice = tier ? tier.price : null;
      var total = estimate.total;
      var canPrice = estimate.available;
      var lines = [];
      var summaryText = [];

      renderTierLadder(totalQuantity, tier);

      root.querySelectorAll('[data-role="digital-total-quantity"]').forEach(function (node) {
        node.textContent = totalQuantity.toLocaleString("en-US") + " pieces";
      });

      var progressMessage = root.querySelector('[data-role="tier-progress-message"]');
      if (progressMessage) {
        if (totalQuantity < digitalMinimum) {
          progressMessage.textContent = "Add " + (digitalMinimum - totalQuantity) + " pieces to reach the minimum.";
        } else if (nextTier) {
          progressMessage.textContent = "Add " + (nextTier.min - totalQuantity) + " pieces to unlock " + money(nextTier.price) + " base pricing.";
        } else {
          progressMessage.textContent = "Best listed cumulative tier unlocked.";
        }
      }

      var status = root.querySelector('[data-role="digital-tier-status"]');
      var baseDisplay = root.querySelector('[data-role="digital-base-price"]');
      if (status) status.textContent = estimate.requiresReview ? "Custom review needed" : (tier ? tier.min + "+ tier" : "Minimum not reached");
      if (baseDisplay) baseDisplay.textContent = tier ? money(tier.price) + " soft-tee base" : digitalMinimum + "-piece combined minimum";

      if (canPrice) {
        digitalState.forEach(function (design, index) {
          var quantity = Math.max(0, integer(design.quantity, 0));
          var option = DIGITAL_PRINT_OPTIONS[design.printOption] || DIGITAL_PRINT_OPTIONS.one_large;
          var garment = findGarment(design.garmentId);
          var estimatedLine = estimate.lines[index];
          var unitPrice = estimatedLine.unitPrice;
          var lineTotal = estimatedLine.subtotal;
          var addonText = estimatedLine.locationAddon
            ? " + " + money(estimatedLine.locationAddon) + " location add-on"
            : " | first print included";

          lines.push([
            '<div class="ttq__summary-line">',
            '<div><strong>' + escapeHtml(design.name || "Untitled design") + " - " + quantity + " " + escapeHtml(garment.name) + "</strong>",
            '<small>' + escapeHtml(option.label) + addonText + "</small></div>",
            '<div class="ttq__summary-line-price"><strong>' + money(unitPrice) + ' each</strong><small>' + money(lineTotal) + " subtotal</small></div>",
            "</div>"
          ].join(""));

          summaryText.push((design.name || "Untitled design") + ": " + quantity + " " + garment.name + ", " + option.label + ", " + money(unitPrice) + " each, " + money(lineTotal) + " subtotal");
        });
      }

      var linesNode = root.querySelector('[data-role="digital-summary-lines"]');
      if (linesNode) {
        if (canPrice) {
          linesNode.innerHTML = lines.join("");
        } else if (estimate.requiresReview) {
          linesNode.innerHTML = '<div class="ttq__callout ttq__callout--warm"><strong>We need to review this combination</strong><ul class="ttq__review-list"><li>' + estimate.reviewReasons.map(escapeHtml).join("</li><li>") + "</li></ul></div>";
        } else {
          linesNode.innerHTML = '<div class="ttq__empty">Build an order of at least ' + digitalMinimum + " combined pieces to see pricing. Each design/garment group needs at least " + digitalGroupMinimum + " pieces.</div>";
        }
      }

      var breakdownNode = root.querySelector('[data-role="digital-breakdown"]');
      if (breakdownNode) {
        breakdownNode.innerHTML = canPrice ? [
          '<div class="ttq__breakdown-row"><span>Combined quantity</span><strong>' + totalQuantity.toLocaleString("en-US") + "</strong></div>",
          '<div class="ttq__breakdown-row"><span>Earned soft-tee tier with first print</span><strong>' + money(tierPrice) + " each</strong></div>",
          '<div class="ttq__breakdown-row"><span>Merchandise subtotal</span><strong>' + money(estimate.pricingSubtotal) + "</strong></div>",
          '<div class="ttq__breakdown-row"><span>Buyer fulfillment (' + estimate.buyerPackages + " package" + (estimate.buyerPackages === 1 ? "" : "s") + ')</span><strong>' + (estimate.fulfillmentTotal ? "+" : "") + money(estimate.fulfillmentTotal) + "</strong></div>"
        ].join("") : "";
      }

      var totalNode = root.querySelector('[data-role="digital-total"]');
      var averageNode = root.querySelector('[data-role="digital-average"]');
      if (totalNode) totalNode.textContent = canPrice ? money(total) : "--";
      if (averageNode) averageNode.textContent = canPrice && totalQuantity ? money(estimate.average) + " average per garment including selected fulfillment" : "Pricing appears when the order qualifies for automatic pricing.";

      root.querySelectorAll('[data-requires="digital-quote"]').forEach(function (button) {
        button.disabled = !canPrice;
      });

      latestDigitalSummary = canPrice ? [
        "Taproom Threads digital print estimate",
        "Combined quantity: " + totalQuantity,
        "Earned cumulative tier: " + tier.min + "+ at " + money(tier.price) + " soft-tee base",
        "",
        summaryText.join("\n"),
        "",
        "Merchandise subtotal: " + money(estimate.pricingSubtotal),
        "Buyer fulfillment: " + estimate.buyerPackages + " packages at " + money(buyerPackagePrice) + " each = " + money(estimate.fulfillmentTotal),
        "Estimated total: " + money(total),
        "Average per piece: " + money(estimate.average),
        "",
        "Bulk packing is included. Estimate excludes shipping, tax, rush service, extended sizes and specialty requests. Final pricing is confirmed after garment, artwork, placement and deadline review."
      ].join("\n") : "";
    }

    function renderScreenGarmentOptions() {
      var select = root.querySelector('[data-field="screen-garment"]');
      if (!select) return;
      select.innerHTML = renderGarmentOptions(screenState.garmentId);
      select.value = screenState.garmentId;
    }

    function renderScreenRows() {
      if (!screenList) return;
      screenList.innerHTML = screenState.locations.map(function (location, index) {
        var placementOptions = Object.keys(SCREEN_PLACEMENT_LABEL).map(function (key) {
          return '<option value="' + key + '"' + (key === location.placement ? " selected" : "") + ">" + SCREEN_PLACEMENT_LABEL[key] + "</option>";
        }).join("");
        var colorOptions = [];
        for (var color = 1; color <= 10; color += 1) {
          colorOptions.push('<option value="' + color + '"' + (color === location.colors ? " selected" : "") + ">" + color + " color" + (color > 1 ? "s" : "") + "</option>");
        }

        return [
          '<div class="ttq__screen-location" data-screen-index="' + index + '">',
          '<div class="ttq__field"><label for="screen-place-' + index + '">Print location ' + (index + 1) + "</label>",
          '<select id="screen-place-' + index + '" data-field="placement">' + placementOptions + "</select></div>",
          '<div class="ttq__field"><label for="screen-colors-' + index + '">Visible ink colors</label>',
          '<select id="screen-colors-' + index + '" data-field="colors">' + colorOptions.join("") + "</select></div>",
          '<button class="ttq__remove" type="button" data-action="remove-screen-location" aria-label="Remove print location ' + (index + 1) + '"' + (screenState.locations.length === 1 ? " disabled" : "") + ">X</button>",
          "</div>"
        ].join("");
      }).join("");

      var addButton = root.querySelector('[data-action="add-screen-location"]');
      if (addButton) addButton.disabled = screenState.locations.length >= 3;
    }

    function calculateScreen() {
      var quantity = Math.max(0, integer(screenState.quantity, 0));
      var isDark = screenState.tone === "dark";
      var screenGarment = findGarment(screenState.garmentId);
      var quantityNode = root.querySelector('[data-role="screen-quantity-display"]');
      if (quantityNode) quantityNode.textContent = quantity.toLocaleString("en-US") + " pieces";

      var estimate = QuoteMath.screenEstimate({
        quantity: quantity,
        minimum: screenMinimum,
        setupPerScreen: screenSetup,
        baseGarment: 7.75 + screenGarment.screenAdjustment,
        tone: screenState.tone,
        locations: screenState.locations
      });

      if (!estimate.available) {
        setScreenUnavailable(estimate.reason);
        return;
      }

      var tier = estimate.tier;
      var totalScreens = estimate.totalScreens;
      var unitPrice = estimate.unitPrice;
      var detailRows = [];
      var summaryRows = [];

      estimate.lines.forEach(function (line, index) {
        var underbaseText = line.hasUnderbase ? " + 1 underbase" : "";
        var label = SCREEN_PLACEMENT_LABEL[line.placement] || "Print location";
        var lineLabel = index === 0
          ? "Garment + first print: " + label
          : "Additional print location: " + label;
        var displayedRate = index === 0
          ? estimate.baseGarment + line.locationRate
          : line.locationRate;
        detailRows.push([
          '<div class="ttq__breakdown-row"><span>' + escapeHtml(lineLabel) + " | " + line.visibleColors + " visible color" + (line.visibleColors > 1 ? "s" : "") + underbaseText + "</span><strong>" + (index === 0 ? "" : "+") + money(displayedRate) + " each</strong></div>"
        ].join(""));
        summaryRows.push(label + ": " + line.visibleColors + " visible color" + (line.visibleColors > 1 ? "s" : "") + (line.hasUnderbase ? " plus underbase" : ""));
      });

      var shirtSubtotal = estimate.shirtSubtotal;
      var setupTotal = estimate.setupTotal;
      var total = estimate.total;
      var digitalTier = QuoteMath.getDigitalTier(tiers, quantity);
      var comparisonAddon = 0;
      if (screenState.locations.length > 1) {
        var secondPlacement = screenState.locations[1].placement;
        comparisonAddon = secondPlacement === "chest" || secondPlacement === "sleeve" ? extraSmallLocation : extraLargeLocation;
      }
      var digitalUnit = digitalTier ? digitalTier.price + screenGarment.digitalAdjustment + comparisonAddon : null;
      var digitalTotal = digitalUnit ? digitalUnit * quantity : null;
      var comparisonText = "";
      var recommendationTitle = "Screen printing estimate";

      if (digitalTotal !== null) {
        var difference = Math.abs(total - digitalTotal);
        if (total < digitalTotal) {
          recommendationTitle = "Screen printing is the better estimate";
          comparisonText = "For this quantity and color setup, screen printing estimates about " + money(difference) + " below comparable digital printing.";
        } else if (total > digitalTotal) {
          recommendationTitle = "Digital printing is the better estimate";
          comparisonText = "The screen setup and color count make digital printing about " + money(difference) + " lower for this order.";
        } else {
          recommendationTitle = "Both methods estimate evenly";
          comparisonText = "Artwork feel, turnaround and reorder plans should decide the production method.";
        }
      }

      var tierNode = root.querySelector('[data-role="screen-tier"]');
      var unitNode = root.querySelector('[data-role="screen-unit-price"]');
      var detailsNode = root.querySelector('[data-role="screen-breakdown"]');
      var shirtSubtotalNode = root.querySelector('[data-role="screen-shirt-subtotal"]');
      var setupNode = root.querySelector('[data-role="screen-setup-total"]');
      var totalNode = root.querySelector('[data-role="screen-total"]');
      var averageNode = root.querySelector('[data-role="screen-average"]');
      var comparisonTitleNode = root.querySelector('[data-role="screen-comparison-title"]');
      var comparisonCopyNode = root.querySelector('[data-role="screen-comparison-copy"]');
      var comparisonNode = root.querySelector('[data-role="screen-comparison"]');

      if (tierNode) tierNode.textContent = tier.toLocaleString("en-US") + "+ print tier";
      if (unitNode) unitNode.textContent = money(unitPrice) + " each";
      if (detailsNode) {
        detailsNode.innerHTML = detailRows.join("");
      }
      if (shirtSubtotalNode) shirtSubtotalNode.textContent = money(shirtSubtotal);
      if (setupNode) setupNode.textContent = money(setupTotal);
      if (totalNode) totalNode.textContent = money(total);
      if (averageNode) averageNode.textContent = money(estimate.average) + " average per garment including setup";
      if (comparisonTitleNode) comparisonTitleNode.textContent = recommendationTitle;
      if (comparisonCopyNode) comparisonCopyNode.textContent = comparisonText;
      if (comparisonNode) comparisonNode.hidden = !comparisonText;

      root.querySelectorAll('[data-requires="screen-quote"]').forEach(function (button) {
        button.disabled = false;
      });

      latestScreenSummary = [
        "Taproom Threads screen printing estimate",
        "Quantity: " + quantity + " " + screenGarment.name + " pieces of one design",
        "Garment: " + screenGarment.name + ", " + (isDark ? "dark garment" : "white or light garment"),
        "Print tier used: " + tier + "+",
        summaryRows.join("\n"),
        "",
        "Decorated garment price: " + money(unitPrice) + " each",
        "Garment subtotal: " + money(shirtSubtotal),
        "Screen setup: " + totalScreens + " screens x " + money(screenSetup) + " = " + money(setupTotal),
        "Estimated total: " + money(total),
        "Average per garment including setup: " + money(estimate.average),
        "",
        recommendationTitle + ": " + comparisonText,
        "",
        "Estimate excludes tax and shipping. Final pricing follows artwork, garment, placement, ink and deadline review."
      ].join("\n");
    }

    function setScreenUnavailable(message) {
      var tierNode = root.querySelector('[data-role="screen-tier"]');
      var unitNode = root.querySelector('[data-role="screen-unit-price"]');
      var detailsNode = root.querySelector('[data-role="screen-breakdown"]');
      var shirtSubtotalNode = root.querySelector('[data-role="screen-shirt-subtotal"]');
      var setupNode = root.querySelector('[data-role="screen-setup-total"]');
      var totalNode = root.querySelector('[data-role="screen-total"]');
      var averageNode = root.querySelector('[data-role="screen-average"]');
      var comparisonNode = root.querySelector('[data-role="screen-comparison"]');
      var noticeNode = root.querySelector('[data-role="screen-unavailable"]');

      if (tierNode) tierNode.textContent = "Custom review";
      if (unitNode) unitNode.textContent = "--";
      if (detailsNode) detailsNode.innerHTML = "";
      if (shirtSubtotalNode) shirtSubtotalNode.textContent = "--";
      if (setupNode) setupNode.textContent = "--";
      if (totalNode) totalNode.textContent = "--";
      if (averageNode) averageNode.textContent = "Enter a qualifying screen print order to see pricing.";
      if (comparisonNode) comparisonNode.hidden = true;
      if (noticeNode) {
        noticeNode.textContent = message;
        noticeNode.hidden = false;
      }
      root.querySelectorAll('[data-requires="screen-quote"]').forEach(function (button) {
        button.disabled = true;
      });
      latestScreenSummary = "";
    }

    function clearScreenUnavailable() {
      var noticeNode = root.querySelector('[data-role="screen-unavailable"]');
      if (noticeNode) noticeNode.hidden = true;
    }

    function sendEmail(summary, subject) {
      if (!summary) return;
      var href = "mailto:" + encodeURIComponent(contactEmail) + "?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(summary);
      window.location.href = href;
    }

    root.addEventListener("click", function (event) {
      var button = event.target.closest("button[data-action]");
      if (!button) return;
      var action = button.dataset.action;

      if (action === "switch-method") {
        var method = button.dataset.method;
        root.querySelectorAll('[data-action="switch-method"]').forEach(function (tab) {
          var selected = tab.dataset.method === method;
          tab.setAttribute("aria-selected", selected ? "true" : "false");
          tab.setAttribute("tabindex", selected ? "0" : "-1");
        });
        root.querySelectorAll("[data-method-panel]").forEach(function (panel) {
          panel.hidden = panel.dataset.methodPanel !== method;
        });
        return;
      }

      if (action === "add-design") {
        addDigitalDesign();
        return;
      }

      if (action === "load-example") {
        digitalState = [
          { id: "example-a", name: "Design A", quantity: 50, garmentId: "soft-cotton-tee", printOption: "one_large" },
          { id: "example-b", name: "Design B", quantity: 50, garmentId: "premium-pullover-hoodie", printOption: "large_small" },
          { id: "example-c", name: "Design C", quantity: 50, garmentId: "average-crewneck", printOption: "two_large" }
        ];
        renderDigitalRows();
        calculateDigital();
        showToast("Three-design example loaded.");
        return;
      }

      if (action === "remove-design") {
        var designRow = button.closest("[data-design-id]");
        if (!designRow || digitalState.length === 1) return;
        digitalState = digitalState.filter(function (design) {
          return design.id !== designRow.dataset.designId;
        });
        renderDigitalRows();
        calculateDigital();
        return;
      }

      if (action === "add-screen-location") {
        if (screenState.locations.length >= 3) return;
        var defaults = ["front", "back", "sleeve"];
        screenState.locations.push({ placement: defaults[screenState.locations.length] || "front", colors: 1 });
        renderScreenRows();
        clearScreenUnavailable();
        calculateScreen();
        return;
      }

      if (action === "remove-screen-location") {
        var locationRow = button.closest("[data-screen-index]");
        if (!locationRow || screenState.locations.length === 1) return;
        screenState.locations.splice(integer(locationRow.dataset.screenIndex, 0), 1);
        renderScreenRows();
        clearScreenUnavailable();
        calculateScreen();
        return;
      }

      if (action === "copy-digital") {
        copyText(latestDigitalSummary, function () { showToast("Digital estimate copied."); });
        return;
      }

      if (action === "email-digital") {
        sendEmail(latestDigitalSummary, "Taproom Threads digital print estimate request");
        return;
      }

      if (action === "copy-screen") {
        copyText(latestScreenSummary, function () { showToast("Screen estimate copied."); });
        return;
      }

      if (action === "email-screen") {
        sendEmail(latestScreenSummary, "Taproom Threads screen printing estimate request");
      }
    });

    root.addEventListener("input", function (event) {
      var designRow = event.target.closest("[data-design-id]");
      if (designRow && event.target.dataset.field) {
        var design = digitalState.find(function (item) {
          return item.id === designRow.dataset.designId;
        });
        if (design) {
          if (event.target.dataset.field === "quantity") {
            design.quantity = Math.max(0, integer(event.target.value, 0));
          } else {
            design[event.target.dataset.field] = event.target.value;
          }
          calculateDigital();
        }
        return;
      }

      if (event.target.matches('[data-field="screen-quantity"]')) {
        screenState.quantity = Math.max(0, integer(event.target.value, 0));
        clearScreenUnavailable();
        calculateScreen();
        return;
      }

      if (event.target.matches('[data-field="buyer-packages"]')) {
        buyerPackages = Math.max(0, integer(event.target.value, 0));
        calculateDigital();
        return;
      }

      if (event.target.matches('[name^="screen-tone-"]')) {
        screenState.tone = event.target.value;
        clearScreenUnavailable();
        calculateScreen();
      }
    });

    root.addEventListener("change", function (event) {
      var designRow = event.target.closest("[data-design-id]");
      if (designRow && (event.target.dataset.field === "printOption" || event.target.dataset.field === "garmentId")) {
        var design = digitalState.find(function (item) {
          return item.id === designRow.dataset.designId;
        });
        if (design) {
          design[event.target.dataset.field] = event.target.value;
          calculateDigital();
        }
        return;
      }

      if (event.target.matches('[data-field="screen-garment"]')) {
        screenState.garmentId = event.target.value;
        clearScreenUnavailable();
        calculateScreen();
        return;
      }

      var locationRow = event.target.closest("[data-screen-index]");
      if (locationRow && event.target.dataset.field) {
        var location = screenState.locations[integer(locationRow.dataset.screenIndex, 0)];
        if (location) {
          if (event.target.dataset.field === "colors") {
            location.colors = Math.max(1, integer(event.target.value, 1));
          } else {
            location[event.target.dataset.field] = event.target.value;
          }
          clearScreenUnavailable();
          calculateScreen();
        }
      }
    });

    addDigitalDesign({ name: "Design A", quantity: 25, garmentId: defaultGarment.id, printOption: "one_large" });
    renderScreenGarmentOptions();
    renderScreenRows();
    calculateScreen();
  }

  function initAll() {
    document.querySelectorAll("[data-tt-quote-builder]").forEach(initBuilder);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
